import { camelCase, chunk, cloneDeep, difference, flatMap, fromPairs, intersection, isArray, isEqual, isNil, isPlainObject, map, mapKeys, mergeWith, omit, partition, pick, times, toPairs, union, uniqBy } from "lodash";
import { pascalCase } from "change-case";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import dayjsTimezone from "dayjs/plugin/timezone.js";
import dayjsUtc from "dayjs/plugin/utc.js";
import { Decimal } from "decimal.js";
import { isValidIBAN } from "ibantools";
import Compressor from "compressorjs";
import { gunzipSync, gzipSync, strFromU8, strToU8 } from "fflate";
import { BaseError as BaseError$1 } from "make-error";
import { serializeError as serializeError$1 } from "serialize-error";
import _deburr from "lodash/deburr.js";
import jsondiffpatch from "jsondiffpatch";
//#region \0rolldown/runtime.js
var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", {
	value,
	configurable: true
});
//#endregion
//#region src/findSmallestPercentValueFromText.ts
const findSmallestPercentValueFromText = (body, threshold) => {
	if (!body?.length) return null;
	const regex = /([\d {2}\\,\\.]+)(([\d ]*%))/g;
	const findings = [];
	let match;
	while ((match = regex.exec(body)) !== null) if (match[1]) findings.push(parseFloatLikeString(match[1]));
	const smallestNumber = findings.length > 0 ? Math.min(...findings) : null;
	return smallestNumber && smallestNumber < threshold ? smallestNumber : null;
};
//#endregion
//#region src/math-helpers.ts
const DECIMALS = 2;
const ROUNDING_ERROR_DECIMALS = 8;
const decimalMultiplier = 10 ** DECIMALS;
const floatingErrorMultiplier = 10 ** ROUNDING_ERROR_DECIMALS;
/**
* in order to cut off floats, we are using up to 8 precision points.
* The diffMultiplier is for determining whether the modulo-op has a remainder of 0.5 with respect of the
* desired rounding-precision
*/
const floatErrorDiffMultiplier = 10 ** (ROUNDING_ERROR_DECIMALS - DECIMALS);
/**
* Rounds a given value by the "bankers-method", with 2 decimal places, the algorithm works as follows:
* 1. Multiply the input-value by 100 (in order to add 2 decimals, multiply by 100 since 10**2)
* 2. Round the multiplied value to standard-rounding-rules
* 3. Apply bankers rounding
* 3.1 If the input-value is subject to bankers-rounding (0.5 as a remainder)
* 3.1.1 Take the rounded value, decrement it to the next even number
* 3.1.2 Return the value with 2 decimal places
* 3.2 If the input is not subject to bankers-rounding
* 3.2.1 Return the value with 2 decimal places
*
*
* Example:
*  Input: 3.4551
*  multiplied by 100: 345.51, remainder: 0.51 (345.51 % 1 = 0.51)
*  0.51 is not subject to bankers-rounding, thus 3.46 is returned (regular integer rounding)
* ---
*  Input: 3.4550
* multiplied by 100: 345.50, remainder: 0.50 (345.50 % 1 = 0.50)
* 0.5 is subject to bankers-rounding, take the rounded-integer-value 346
* After rounding, the resulting value is even, it can be directly returned (3.46).
* ---
* Input: 3.4650
* multiplied by 100: 346.50, remainder: 0.50 (346.50 % 1 = 0.50)
* 0.5 is subject to bankers-rounding, take the rounded-integer-value 347
* After rounding, the resulting value is uneven, subtract 1 to get the next even-value (3.46)
*
*/
const roundByBankersMethod = (x) => {
	if (x === null) return x;
	const rounded = Math.round(x * decimalMultiplier);
	return isHalf(x) ? decrementOnUneven(rounded) / decimalMultiplier : rounded / decimalMultiplier;
};
const isHalf = (n) => Math.abs(n * floatingErrorMultiplier | 0) % floatErrorDiffMultiplier === .5 * floatErrorDiffMultiplier;
const isNumberEven = (n) => n % 2 === 0;
const decrementOnUneven = (n) => isNumberEven(n) ? n : n - 1;
const transpose = (arr) => {
	if (!arr.length) return [];
	const width = Math.max(...arr.map((row) => row.length));
	return Array.from({ length: width }, (_, i) => arr.map((row) => row[i]));
};
const roundAccurately = (num, precision = 0) => {
	if (Number.isNaN(num) || Number.isNaN(precision) || !(typeof precision === "number")) return NaN;
	if (precision === 0) return +Math.round(num).toFixed(0);
	return Number(`${Math.round(`${num}e${precision}`)}e-${precision}`);
};
let BasecampFilterSpecifics = /* @__PURE__ */ function(BasecampFilterSpecifics) {
	BasecampFilterSpecifics["duplicates"] = "duplicates";
	BasecampFilterSpecifics["unpublished"] = "unpublished";
	return BasecampFilterSpecifics;
}({});
function decimalAdjust(type, value, exp) {
	if (typeof exp === "undefined" || +exp === 0) return Math[type](value);
	value = +value;
	exp = +exp;
	if (Number.isNaN(value) || !(exp % 1 === 0)) return NaN;
	let expStr = value.toString().split("e");
	value = Math[type](+`${expStr[0]}e${expStr[1] ? +expStr[1] + exp : exp}`);
	expStr = value.toString().split("e");
	return +`${expStr[0]}e${expStr[1] ? +expStr[1] - exp : -exp}`;
}
const round = (value, exp) => decimalAdjust("round", value, exp);
const floor = (value, exp) => decimalAdjust("floor", value, exp);
const splitAmountAcrossAllocations = (totalAmount, allocationWeights) => {
	const weights = allocationWeights.map((weight) => new Decimal(weight ?? 0));
	const allocationCount = weights.length;
	if (allocationCount === 0) return [];
	const totalCents = totalAmount.mul(100).toDecimalPlaces(0);
	const totalWeight = weights.reduce((sum, weight) => sum.plus(weight), new Decimal(0));
	if (totalWeight.lte(0)) {
		const baseCents = totalCents.div(allocationCount).floor();
		const remainderCents = totalCents.mod(allocationCount).toNumber();
		return Array.from({ length: allocationCount }, (_, index) => {
			return (index < remainderCents ? baseCents.plus(1) : baseCents).div(100).toDecimalPlaces(2);
		});
	}
	const baseCentsPerAllocation = weights.map((weight) => totalCents.mul(weight).div(totalWeight).floor());
	const centsAlreadyAllocated = baseCentsPerAllocation.reduce((sum, cents) => sum.plus(cents), new Decimal(0));
	const remainingCents = totalCents.minus(centsAlreadyAllocated).toNumber();
	const indexesByBiggestRemainder = weights.map((weight, index) => ({
		index,
		remainder: totalCents.mul(weight).div(totalWeight).minus(baseCentsPerAllocation[index])
	})).sort((a, b) => b.remainder.comparedTo(a.remainder) || a.index - b.index).map((item) => item.index);
	return baseCentsPerAllocation.map((baseCents, index) => {
		return (indexesByBiggestRemainder.slice(0, remainingCents).includes(index) ? baseCents.plus(1) : baseCents).div(100).toDecimalPlaces(2);
	});
};
const median = (numbers) => {
	if (!numbers?.length) return 0;
	const middle = (numbers.length + 1) / 2;
	const sorted = [...numbers].sort((a, b) => a - b);
	return sorted.length % 2 === 0 ? (sorted[middle - 1.5] + sorted[middle - .5]) / 2 : sorted[middle - 1];
};
const getPsValue = (p, list) => {
	if (p === 0) return list[0];
	return list[Math.ceil(list.length * (p / 100)) - 1];
};
const percentile = (pOrPs, list) => {
	const ps = Array.isArray(pOrPs) ? pOrPs : [pOrPs];
	list = list.slice().sort((a, b) => {
		a = Number.isNaN(a) ? Number.NEGATIVE_INFINITY : a;
		b = Number.isNaN(b) ? Number.NEGATIVE_INFINITY : b;
		if (a > b) return 1;
		if (a < b) return -1;
		return 0;
	});
	if (ps.length === 1) return [getPsValue(ps[0], list)];
	return ps.map((p) => getPsValue(p, list));
};
const safeSum = (...nums) => {
	return nums.reduce((acc, cur) => {
		if (isNil(cur) || Number.isNaN(cur)) return acc;
		return acc + cur;
	}, 0);
};
const safeDifference = (...nums) => {
	if (!nums.length) return 0;
	if (nums.length === 1) return nums[0] ?? 0;
	const [start, ...rest] = nums;
	return rest.reduce((acc, cur) => {
		if (isNil(cur) || Number.isNaN(cur)) return acc;
		return acc - cur;
	}, start ?? 0);
};
function safeMul(...values) {
	return values.reduce((acc, v) => {
		if (v == null || isNaN(v)) return 0;
		return acc * v;
	}, 1);
}
function safeDivide(numerator, divisor) {
	if (numerator == null || divisor == null || divisor === 0) return null;
	return numerator / divisor;
}
const floatEquals = (a, b, delta = Number.EPSILON) => {
	if (isNil(a) || isNil(b)) return a === b;
	return Math.abs(a - b) < delta;
};
const floatBiggerThan = (a, b, delta = Number.EPSILON) => {
	if (floatEquals(a, b, delta)) return false;
	return a > b;
};
const floatSmallerOrEqualThan = (a, b, delta = Number.EPSILON) => {
	return !floatBiggerThan(a, b, delta);
};
/**
* Percentage ratio of a to b
*/
const comparisonRatioPercentage = (a, b) => {
	if (isNil(a) || isNil(b) || b === 0) return 0;
	return round(a / b * 100, 2);
};
//#endregion
//#region src/iban.ts
const iso13616Prepare = (iban) => {
	let usedIban = iban.toUpperCase();
	usedIban = `${usedIban.substr(4)}${usedIban.substr(0, 4)}`;
	const A = "A".charCodeAt(0);
	const Z = "Z".charCodeAt(0);
	return usedIban.split("").map((n) => {
		const code = n.charCodeAt(0);
		if (code >= A && code <= Z) return code - A + 10;
		return n;
	}).join("");
};
/**
* Calculates the MOD 97 10 of the passed IBAN as specified in ISO7064.
*/
const ibanChecksum = (iban) => {
	let remainder = iban;
	let block;
	while (remainder.length > 2) {
		block = remainder.slice(0, 9);
		remainder = Number.parseInt(block, 10) % 97 + remainder.slice(block.length);
	}
	return Number.parseInt(remainder, 10) % 97;
};
const leftPad = (paddableString, length, padContent = "0") => {
	if (paddableString.length >= length) return paddableString;
	return `${times(length - paddableString.length, () => padContent).join("")}${paddableString}`;
};
const bankAccountLengthMap = { DE: 10 };
/**
* Creates an iban from country-code, bank-code and bankAccountId.
*
* Note the process for  creating checkDigits is documented here
* https://en.wikipedia.org/wiki/International_Bank_Account_Number#Generating_IBAN_check_digits

*/
const createIban = (countryCode, bankCode, bankAccountId) => {
	const bankAccountLength = bankAccountLengthMap[countryCode];
	if (!bankAccountLength) throw new Error(`Unknown country ${countryCode}`);
	const baseIban = `${bankCode}${leftPad(bankAccountId, bankAccountLength)}`;
	return `${countryCode}${`0${98 - ibanChecksum(iso13616Prepare(`${countryCode}00${baseIban}`))}`.slice(-2)}${baseIban}`;
};
/**
* Extracts IBAN from a string.
* DE51KBAGCH22CH69007610443247XXXXX => CH69007610443247XXXXX
* @param str string to extract IBAN from
* @returns IBAN or undefined if not found
*/
const extractIBAN = (str) => {
	if (!str) return;
	if (isValidIBAN(str)) return str;
	const regex = /[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}/g;
	let runner;
	let lastMatch;
	while ((runner = regex.exec(str)) !== null) {
		lastMatch = runner[0];
		regex.lastIndex = runner.index + 1;
	}
	return lastMatch;
};
//#endregion
//#region src/mixed.ts
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);
let DateFormats = /* @__PURE__ */ function(DateFormats) {
	DateFormats["SimpleDateReversed"] = "YYYY.MM.DD";
	DateFormats["BasicShortDateReversed"] = "YYMMDD";
	DateFormats["BasicDateReversed"] = "YYYYMMDD";
	DateFormats["SafeDatetime"] = "YYYY-MM-DD_HH-mm-ss";
	DateFormats["ShortDate"] = "MMM, YYYY";
	DateFormats["SimpleDate"] = "DD.MM.YYYY";
	DateFormats["SnakeDate"] = "DD_MM_YYYY";
	DateFormats["SimpleDatetime"] = "DD.MM.YYYY HH:mm:ss";
	DateFormats["SimpleTime"] = "HH:mm:ss";
	DateFormats["DateTime"] = "YYYY.MM.DD HH:mm:ss";
	DateFormats["FullDate"] = "DD. MMMM YYYY";
	DateFormats["MonthNameAndYearDate"] = "MMMM YYYY";
	DateFormats["ISO8601"] = "YYYY-MM-DD";
	DateFormats["USDate"] = "MM/DD/YYYY";
	DateFormats["SpreadsheetDate"] = "YYYY/MM/DD";
	return DateFormats;
}({});
const getCCSBaseUrl = (environmentName) => {
	if (environmentName === "local") return `http://localhost:8081`;
	if (environmentName === "dev") return `https://ccs.dev.condogroup.tech`;
	if (environmentName === "stg") return `https://ccs.stg.condogroup.tech`;
	if (environmentName === "prod") return `https://ccs.condogroup.tech`;
	return "#";
};
const getInngestUrl = (environmentName, inngestId) => {
	if (environmentName === "local") return `http://localhost:8288/run?runID=${inngestId}`;
	if (environmentName === "dev") return `https://app.inngest.com/env/dev-acc54c75/runs/${inngestId}`;
	if (environmentName === "stg") return `https://app.inngest.com/env/staging-72692b17/runs/${inngestId}`;
	if (environmentName === "prod") return `https://app.inngest.com/env/production/runs/${inngestId}`;
	return "#";
};
const estimateJSONByteSize = (data) => {
	if ("Buffer" in globalThis) return globalThis.Buffer.byteLength(JSON.stringify(data), "utf8");
	if ("Blob" in globalThis) return new globalThis.Blob([JSON.stringify(data)]).size;
	throw new Error(`Neither Buffer nor Blob available, cannot calculate byte-size`);
};
const arrayify = (val) => [].concat(val);
const stripEmptyFields = (obj) => Object.entries(obj).reduce((acc, [key, value]) => {
	if (isNil(value) || typeof value === "string" && value.length === 0 || Array.isArray(value) && value.length === 0) return acc;
	return {
		...acc,
		[key]: value
	};
}, {});
const decapitalize = (string) => string.charAt(0).toLowerCase() + string.slice(1);
const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);
const formatDate = (val, format, locale) => {
	let date = dayjs(val);
	if (locale) date = date.locale(locale);
	return date.format(format || "YYYY-MM-DD");
};
/**
* Converts a string to PascalCase notation, empty strings to undefined
* @param val
*/
const enumPascal = (val) => {
	if (!val || val.length === 0) return;
	return pascalCase(val);
};
/**
* checks if a date is today
*/
const isToday = (date) => {
	return dayjs(date).isSame(/* @__PURE__ */ new Date(), "d");
};
/**
* rounds a number to the nearest number with the option to have a maximum value rounding
*/
const roundToNearest = (num, nearestNumber, { max } = {}) => {
	const roundedNumber = Math.round(num / nearestNumber) * nearestNumber;
	if (max && roundedNumber > max) return max;
	return roundedNumber;
};
const roundUpToNearest = (price, nearest = 1e3) => {
	return Math.ceil(price / nearest) * nearest;
};
/**
* YES, true => true
* everything else to false
* @param val
*/
const toBoolean = (val) => {
	if (typeof val === "string") return [
		"true",
		"yes",
		"t",
		"j",
		"y",
		"available"
	].includes(val.toLowerCase());
	return val;
};
function getFreeFromCategory(vacantFrom, publishingDate) {
	if (!vacantFrom) return;
	let freeFromCategory;
	let validDate;
	for (const format of ["DD.MM.YYYY", "D MMMM YYYY"]) if (dayjs(vacantFrom, format).isValid()) {
		validDate = dayjs(vacantFrom, format);
		break;
	}
	if (validDate && publishingDate) {
		const month = validDate.diff(publishingDate, "month");
		switch (true) {
			case month <= 1:
				freeFromCategory = "ASAP";
				break;
			case month <= 3:
				freeFromCategory = "1<X<3";
				break;
			case month <= 6:
				freeFromCategory = "3<X<6";
				break;
			case month > 6:
				freeFromCategory = "X>6";
				break;
			default: freeFromCategory = "ASAP";
		}
	} else {
		const vacancyString = vacantFrom.toLowerCase();
		switch (true) {
			case vacancyString.toLowerCase().includes("sofor"):
			case vacancyString.toLowerCase().includes("kurz"):
				freeFromCategory = "ASAP";
				break;
			case vacancyString.toLowerCase().includes("absprache"):
			case vacancyString.toLowerCase().includes("vereinbarung"):
			case vacancyString.toLowerCase().includes("vb"):
				freeFromCategory = "ByAgreement";
				break;
			default: freeFromCategory = void 0;
		}
	}
	return freeFromCategory;
}
const cleanNonPrintableChars = (text) => {
	return text.replace(/[\x00-\x1F\x7F]/g, "");
};
function escapeNumber(val, decimals = 2, defaultVal = null) {
	const decMultiplier = 10 ** decimals;
	if (Number.isNaN(val) || !Number.isFinite(val) || val < 0) return defaultVal;
	return Math.round(val * decMultiplier) / decMultiplier;
}
function calculateDiffInYears(fromYear, toYear) {
	if (+toYear < 0) return null;
	return escapeNumber(fromYear.diff(dayjs(`${toYear}`, "YYYY"), "year"));
}
function energyCategoryMapper(consumption) {
	let category;
	switch (true) {
		case isNil(consumption):
			category = null;
			break;
		case consumption >= 0 && consumption <= 30:
			category = "A+";
			break;
		case consumption <= 50:
			category = "A";
			break;
		case consumption <= 75:
			category = "B";
			break;
		case consumption <= 100:
			category = "C";
			break;
		case consumption <= 130:
			category = "D";
			break;
		case consumption <= 160:
			category = "E";
			break;
		case consumption <= 200:
			category = "F";
			break;
		case consumption <= 250:
			category = "G";
			break;
		case consumption > 250:
			category = "H";
			break;
		default: category = null;
	}
	return category;
}
function cleanStreetName(street) {
	if (!street) return null;
	const cleanStreet = street.replace(/[0-9]/g, "").trim();
	if (cleanStreet.length < 3) return null;
	return cleanStreet;
}
function parseNumbers(value) {
	return value.match(/\d([\d+.,]?)+/g);
}
function parseFloatLikeString(str) {
	/**
	* Leaves only last delimiter
	* If this delimiter is comma - replace it with dot
	* Parses result
	*/
	return Number.parseFloat(str.replace(/[ ]/g, "").replace(/[., ](?=.*[.,])/g, "").replace(",", "."));
}
function getCourtageMultiplier({ value, purchasePrice, description }) {
	if (value?.toLowerCase().includes("provisionsfrei")) return 0;
	let courtageMultiplier;
	const firstMatch = parseNumbers(value.replace(/ /g, ""))?.shift();
	if (!firstMatch) courtageMultiplier = findSmallestPercentValueFromText(description, 10);
	else courtageMultiplier = parseFloatLikeString(firstMatch);
	if (!courtageMultiplier || Number.isNaN(courtageMultiplier)) return null;
	if (courtageMultiplier / 100 < 1) return round(courtageMultiplier / 100, 5);
	return round(courtageMultiplier / purchasePrice, 5);
}
function getYearsSinceRenovation(constructionYear, lastRefurbishment, condition) {
	const now = dayjs();
	if (constructionYear === now.year()) return 0;
	if (!constructionYear && condition === "FirstTimeUse") return 0;
	return calculateDiffInYears(now, lastRefurbishment);
}
function getAddressLine({ street, houseNumber, zipcode, quarter, city }) {
	const basePart = [street, houseNumber].filter(Boolean);
	if (quarter) basePart.push(quarter);
	const zipCodeCityPart = [zipcode, city].filter(Boolean);
	return [basePart.join(" "), zipCodeCityPart.join(" ")].join(basePart.length ? ", " : "");
}
const knownDomains = {
	IS24: "www.immobilienscout24.de",
	IMMONET: "www.immonet.de",
	DEKLEINANZEIGEN: "de.kleinanzeigen.com"
};
const umlautMap = {
	Ä: "Ae",
	ä: "ae",
	Ö: "Oe",
	ö: "oe",
	Ü: "Ue",
	ü: "ue",
	ß: "ss"
};
const replaceAllWithMap = (str, mapObj) => {
	const re = new RegExp(Object.keys(mapObj).join("|"), "gi");
	return str.replace(re, (matched) => mapObj[matched.toLowerCase()]);
};
const sleep = (timeMs, unref = true) => new Promise((resolve) => {
	const timer = setTimeout(resolve, timeMs);
	if (unref) timer.unref();
});
function isString(value) {
	return typeof value === "string";
}
function isObject(value) {
	return value !== null && typeof value === "object";
}
/**
* Creates an object which only contains the (deeply) changed fields (new + updated) between two objects
* @returns changed fields with values from the **second** object
*
* - Arrays are treated as a single value, if the array is different, the whole array is returned
* - Supports Date objects
*
* @example
* c1:      { a: { b: 1, d: 42 }, c: [1,2] }
* c2:      { a: { b: 2, d: 42 }, e: [1,2,3] }
*          (d untouched, b changed, c, removed, e added)
* returns: { a: { b: 2 }, c: undefined, e: [1,2,3] }
*/
function diff(c1, c2) {
	if (!c1) return c2 || {};
	if (!c2) return c1 || {};
	if (c1 instanceof Date || c2 instanceof Date) {
		if (dayjs(new Date(String(c1))).utc().unix() === dayjs(new Date(String(c2))).utc().unix()) return {};
		return c2;
	}
	const updates = Object.keys(c1).reduce((acc, key) => {
		const v1 = c1[key];
		const v2 = c2[key];
		if (Array.isArray(v1) && !isEqual(v1, v2)) {
			acc[key] = v2;
			return acc;
		}
		if (isObject(v1)) {
			const patch = diff(v1, v2);
			if (Object.keys(patch).length > 0 || patch instanceof Date) acc[key] = patch;
			return acc;
		}
		if (v1 !== v2) {
			acc[key] = v2;
			return acc;
		}
		return acc;
	}, {});
	const newFields = difference(Object.keys(c2), Object.keys(c1));
	return {
		...updates,
		...pick(c2, newFields)
	};
}
/**
* Creates an object which only contains the (deeply) changed fields (new + updated) between two objects
* @returns an object containing changed fields in the shape of `{ [field]: { from: oldValue, to: newValue } }`
*
* - Arrays are treated as a single value, if the array is different, the whole array is returned
* - Supports Date objects
*/
function superDelta(c1, c2) {
	if (!c1) return c2 || {};
	if (!c2) return c1 || {};
	if (c1 instanceof Date || c2 instanceof Date) {
		if (dayjs(new Date(String(c1))).utc().unix() === dayjs(new Date(String(c2))).utc().unix()) return {};
		return c2;
	}
	const updates = Object.keys(c1).reduce((acc, key) => {
		const v1 = c1[key];
		const v2 = c2[key];
		if (Array.isArray(v1) && !isEqual(v1, v2)) {
			acc[key] = {
				from: v1,
				to: v2
			};
			return acc;
		}
		if (isObject(v1)) {
			const patch = superDelta(v1, v2);
			if (Object.keys(patch).length > 0) acc[key] = patch;
			return acc;
		}
		if (v1 !== v2) {
			acc[key] = {
				from: v1,
				to: v2
			};
			return acc;
		}
		return acc;
	}, {});
	const newFields = difference(Object.keys(c2), Object.keys(c1));
	return Object.keys(c2).filter((key) => newFields.includes(key)).reduce((acc, key) => {
		acc[key] = {
			from: void 0,
			to: c2[key]
		};
		return acc;
	}, updates);
}
function isPresent(value) {
	if (isNil(value)) return false;
	if (typeof value === "number") return true;
	return Boolean(value.length);
}
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const firstFiniteNumber = (...values) => {
	for (const value of values) if (typeof value === "number" && Number.isFinite(value)) return value;
	return null;
};
const firstNonEmptyString = (...values) => {
	for (const value of values) if (typeof value === "string" && value.trim() !== "") return value;
	return null;
};
const arrify = (v) => [].concat(v);
const getGDriveLink = (folderId) => `https://drive.google.com/drive/folders/${folderId}`;
const subSort = (arr, startIdx, numElements, sortFn) => [].concat(...arr.slice(0, startIdx), ...arr.slice(startIdx, startIdx + numElements).sort(sortFn), ...arr.slice(startIdx + numElements, arr.length));
const isOpenDateIntervalsOverlapping = (a, b, tz = "utc") => {
	return a.some((left) => {
		if (b.some((right) => !right.endDate && (!left.endDate || dayjs(right.startDate).tz(tz).isBefore(dayjs(left.endDate).tz(tz), "d")))) return true;
		if (!left.endDate && b.some((right) => dayjs(right.endDate).tz(tz).isAfter(dayjs(left.startDate).tz(tz), "d"))) return true;
		if (b.some((right) => dayjs(left.startDate).tz(tz).isBefore(dayjs(right.endDate).tz(tz), "d") && dayjs(left.endDate).tz(tz).isAfter(dayjs(right.startDate).tz(tz), "d"))) return true;
		return false;
	});
};
const getPrefixSumArrayWithInflation = (initialValue, inflationRate, length, initialPrefixSumWithInflation) => {
	const prefixSumWithInflation = initialPrefixSumWithInflation ?? [];
	for (let i = prefixSumWithInflation.length; i < length; i++) {
		const previous = prefixSumWithInflation[i - 1];
		prefixSumWithInflation.push(previous ? previous + previous * inflationRate : initialValue);
	}
	return prefixSumWithInflation;
};
const definedAndEquals = (a, b, comparator) => {
	if (isNil(a) || isNil(b)) return false;
	return comparator ? comparator(a, b) : a === b;
};
const generateRandomPassword = (passwordLength = 10) => {
	const chars = "0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ";
	let password = "";
	for (let i = 0; i < passwordLength; i++) {
		const randomNumber = Math.floor(Math.random() * 72);
		password += chars.substring(randomNumber, randomNumber + 1);
	}
	return password;
};
function keysToCamelCase(obj) {
	return mapKeys(obj, (_value, key) => camelCase(key));
}
const flattenJSON = (jsonObj) => {
	return fromPairs(flatMap(toPairs(jsonObj), ([key, value]) => {
		if (typeof value === "object" && value !== null) return map(flattenJSON(value), (innerValue, innerKey) => [`${key}.${innerKey}`, innerValue]);
		return [[key, value]];
	}));
};
const replacePathname = (dbUrl, nextPath) => {
	const url = new URL(dbUrl);
	url.pathname = `/${nextPath}`;
	return url.toString();
};
const getMonthlyChunks = (startDate, endDate, maxDaysPerChunk) => {
	const start = dayjs(startDate);
	const end = dayjs(endDate);
	const chunks = [];
	let currentDate = start;
	while (currentDate.isSameOrBefore(end, "day")) {
		const endOfMonth = currentDate.endOf("month");
		const chunkEnd = end.isBefore(endOfMonth) ? end : endOfMonth;
		if (!maxDaysPerChunk) {
			chunks.push({
				startDate: currentDate.startOf("day").toDate(),
				endDate: chunkEnd.endOf("day").toDate()
			});
			currentDate = currentDate.add(1, "month").startOf("month");
			continue;
		}
		if (chunkEnd.diff(currentDate, "day") + 1 <= maxDaysPerChunk) {
			chunks.push({
				startDate: currentDate.startOf("day").toDate(),
				endDate: chunkEnd.endOf("day").toDate()
			});
			currentDate = currentDate.add(1, "month").startOf("month");
		} else {
			let chunkStart = currentDate;
			while (chunkStart.isSameOrBefore(chunkEnd, "day")) {
				const potentialChunkEnd = chunkStart.add(maxDaysPerChunk - 1, "day");
				const actualChunkEnd = chunkEnd.isBefore(potentialChunkEnd) ? chunkEnd : potentialChunkEnd;
				chunks.push({
					startDate: chunkStart.startOf("day").toDate(),
					endDate: actualChunkEnd.endOf("day").toDate()
				});
				chunkStart = actualChunkEnd.add(1, "day");
			}
			currentDate = currentDate.add(1, "month").startOf("month");
		}
	}
	return chunks;
};
/**
* Removes any nested objects from the object leaving only the top-level keys that are not objects (with exception of Date objects)
* @param obj
* @returns a deep clone of the object with all nested objects removed
*/
const prune = (obj) => {
	const clone = cloneDeep(obj);
	return Object.keys(clone).reduce((pruned, key) => {
		if (typeof obj[key] === "object" && !(obj[key] instanceof Date)) return pruned;
		pruned[key] = obj[key];
		return pruned;
	}, {});
};
const roundToNearestMinute = (date, minute) => {
	const ms = 1e3 * 60 * minute;
	return new Date(Math.round(date.getTime() / ms) * ms);
};
/**
* Useful for checking presence of ids or presence of objects
*
* if you use this helper be aware of values that are coerced to falsy or truthy (like 0, '', null, undefined)
*/
const xor = (a, b) => !a !== !b;
const safeTry = (fn, or = void 0) => {
	try {
		return fn();
	} catch (e) {
		return or;
	}
};
const valOrNil = (value) => {
	if (value === null || typeof value === "undefined") return value;
	return value;
};
/**
* Transposes an array of objects into an object of arrays.
* Converts [{ a: 1, b: 2 }, { a: 3, b: 4 }] into { a: [1, 3], b: [2, 4] }
*/
const transposeObjects = (objects, options) => {
	if (!objects || objects.length === 0) return {};
	const excludeSet = new Set(options?.exclude ?? []);
	const result = {};
	const keys = Object.keys(objects[0]).filter((key) => !excludeSet.has(key));
	for (const key of keys) result[key] = objects.map((obj) => obj[key] ?? null);
	return result;
};
//#endregion
//#region src/async-utilities.ts
const batchedConcurrentAllSettled = async (items, workFn, concurrency, opts) => {
	const chunks = chunk(items, concurrency);
	const results = [];
	for (const [chunkIdx, chunk] of chunks.entries()) {
		const chunkResults = await Promise.allSettled(chunk.map((item, idx) => workFn(item, chunkIdx * concurrency + idx)));
		results.push(...chunkResults);
		if (opts?.onBatchProcessed) opts.onBatchProcessed(chunkIdx);
	}
	const [failed, succeeded] = partition(results, (r) => r.status === "rejected");
	return {
		failed: failed.map((s) => s.reason),
		succeeded: succeeded.map((s) => s.value)
	};
};
const batchedConcurrentAll = async (items, workFn, concurrency, opts) => {
	if (!items?.length) return [];
	const chunks = chunk(items, concurrency);
	const results = [];
	for (const [chunkIdx, chunk] of chunks.entries()) {
		const chunkResults = await Promise.all(chunk.map((item, idx) => workFn(item, chunkIdx * concurrency + idx)));
		results.push(...chunkResults);
		if (opts?.onBatchProcessed) opts.onBatchProcessed(chunkIdx);
	}
	return results;
};
const asyncRetry = async (promFn, maxTries, sleepMs, { strategy } = { strategy: "linear" }) => {
	let tries = 0;
	let lastError;
	while (tries < maxTries) try {
		return await promFn();
	} catch (e) {
		tries++;
		lastError = e;
		if (sleepMs) {
			await sleep(sleepMs);
			if (strategy === "exponential") sleepMs = Math.min(2 ** tries + sleepMs / 1e3, 32) * 1e3;
		}
	}
	throw lastError || /* @__PURE__ */ new Error("Timeout while waiting for prom-fn to finish");
};
var LazyPromise = class {
	executor;
	promise;
	constructor(executor) {
		this.executor = executor;
		this.promise = null;
	}
	then(onfulfilled, onrejected) {
		this.initPromise();
		return this.promise?.then(onfulfilled, onrejected);
	}
	catch(onrejected) {
		this.initPromise();
		return this.promise?.catch(onrejected);
	}
	finally(onfinally) {
		this.initPromise();
		return this.promise?.finally(onfinally);
	}
	initPromise() {
		if (!this.promise) this.promise = new Promise(this.executor);
	}
};
const withTimeout = async (promise, ms, error) => {
	let timeout;
	const timeoutPromise = new Promise((_, reject) => {
		const err = error || /* @__PURE__ */ new Error("Operation is timed out");
		err.code = "TIMEOUT";
		err.data = {
			...err.data,
			timeoutMs: ms
		};
		timeout = setTimeout(() => reject(err), ms);
	});
	return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeout));
};
const ensureMinimumTime = async (promise, ms) => {
	const timerPromise = new Promise((resolve) => setTimeout(resolve, ms));
	const [result] = await Promise.allSettled([promise(), timerPromise]);
	if (result.status === "rejected") throw result.reason;
	return result.value;
};
//#endregion
//#region src/compress-image.ts
const downscaleImageFiles = async (fileOrFiles, maxSize = 1e3) => {
	return Promise.all(arrify(fileOrFiles).map(async (f) => {
		if (f.type.startsWith("image")) return new Promise((resolve, reject) => {
			new Compressor(f, {
				quality: .6,
				maxHeight: maxSize,
				maxWidth: maxSize,
				success: (file) => {
					resolve(new File([file], f.name, { type: file.type }));
				},
				error: reject
			});
		});
		return f;
	}));
};
//#endregion
//#region src/compress-response.ts
const EMPTY_VAL = "߷";
const COL_SEPARATOR = "ߟ";
const ROW_SEPARATOR = "ߡ";
const compressResponse = (data, compressFn) => {
	const compressed = gzipSync(strToU8(data.map((date) => compressFn(date).join("ߟ")).join("ߡ")));
	return Buffer.from(compressed).toString("base64");
};
const undefinedWhenEmpty = (val, mapper) => val === "߷" ? void 0 : mapper(val);
const decompressResponse = (data, decompressFn) => {
	const decompressed = strFromU8(gunzipSync(Uint8Array.from(atob(data), (c) => c.charCodeAt(0))));
	return decompressed ? decompressed.split("ߡ").map((r) => decompressFn(r.split("ߟ"))) : [];
};
//#endregion
//#region src/date.ts
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(dayjsTimezone);
dayjs.extend(dayjsUtc);
const UNIX_EPOCH_SPREADSHEET_SERIAL_DATE = 25569;
const SPREADSHEET_DATE_FORMATS = ["YYYY-MM-DD", "YYYY/MM/DD"];
const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;
const extractDate = (d) => {
	if (!d || d instanceof Date && Number.isNaN(d)) throw new Error("Provided date is invalid");
	if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
	const date = d instanceof Date ? new Date(d.getTime()) : dayjs.utc(d).toDate();
	date.setHours(4, 0, 0, 0);
	return date.toISOString().substring(0, 10);
};
const sameDay = (d1, d2) => extractDate(d1) === extractDate(d2);
const isBefore = (d1, d2) => dayjs(d1).isBefore(d2, "d");
const sortByDate = (list, key, direction = "asc") => list.toSorted((a, b) => {
	const dateA = new Date(a[key]).getTime();
	const dateB = new Date(b[key]).getTime();
	return direction === "asc" ? dateA - dateB : dateB - dateA;
});
const dateToSpreadsheetDate = (date) => {
	const day = dayjs.isDayjs(date) ? date.utc().startOf("day") : dayjs.utc(date).startOf("day");
	return UNIX_EPOCH_SPREADSHEET_SERIAL_DATE + day.diff(dayjs.utc("1970-01-01"), "day");
};
const parseSpreadsheetDate = (dateString, formats) => {
	for (const format of formats) {
		const parsed = dayjs.utc(dateString, format, true);
		if (parsed.isValid()) return dateToSpreadsheetDate(parsed);
	}
	return null;
};
const dateStringToSpreadsheetDate = (dateString, formats = SPREADSHEET_DATE_FORMATS) => {
	const trimmed = dateString.trim();
	const serialDate = parseSpreadsheetDate(trimmed, formats);
	if (serialDate !== null) return serialDate;
	return ISO_DATE_PREFIX.test(trimmed) ? parseSpreadsheetDate(trimmed.slice(0, 10), formats) : null;
};
const calculatePartialMonthRatio = (month, startDate, endDate) => {
	const monthStartDate = dayjs(month).tz("Europe/Berlin").startOf("month");
	const monthEndDate = dayjs(month).tz("Europe/Berlin").endOf("month");
	const daysInMonth = dayjs(month).daysInMonth();
	const payoutStartDate = monthStartDate.isSame(dayjs(startDate).tz("Europe/Berlin"), "month") ? dayjs(startDate).tz("Europe/Berlin") : monthStartDate;
	return (dayjs(endDate && monthEndDate.isSame(dayjs(endDate).tz("Europe/Berlin"), "month") ? dayjs(endDate).tz("Europe/Berlin") : monthEndDate).diff(payoutStartDate, "day") + 1) / daysInMonth;
};
const arePeriodsOverlapping = (period1, period2) => {
	const farFuture = dayjs("9999-12-31");
	const p1Start = dayjs(period1.startDate);
	const p1End = period1.endDate ? dayjs(period1.endDate) : farFuture;
	const p2Start = dayjs(period2.startDate);
	const p2End = period2.endDate ? dayjs(period2.endDate) : farFuture;
	return p1Start.isSameOrBefore(p2End, "day") && p2Start.isSameOrBefore(p1End, "day");
};
//#endregion
//#region src/days360.ts
/**
* WHOLE FILE TAKEN FROM INTERNET
*/
function formatDate$1(value) {
	return `${value.getUTCFullYear()}${value.getUTCMonth()}${value.getUTCDate()}`;
}
__name(formatDate$1, "formatDate");
/**
* Check if a date is the last day of February
*
* @param   {Date}      value   Date to validate
* @returns {Boolean}   true if the last day of February
*/
const isLastDay = (value) => {
	if (value.getUTCMonth() === 1) return formatDate$1(new Date(value.getUTCFullYear(), 2, 0)) === formatDate$1(value);
	return false;
};
const isNumber = (value) => {
	const { toString } = Object.prototype;
	return toString.call(value) === "[object Number]" && value === +value;
};
const isDate = (value) => {
	const { toString } = Object.prototype;
	if (toString.call(value) === "[object Date]") return isNumber(value.valueOf());
	return false;
};
/**
* Constants
*/
const US = 0;
const EU = 1;
const US_NASD = 2;
/**
* Calculates the nubmer of days between two dates based on a 360-day year, using the US/NASD
* method (30US/360) or European method (30E/360).
*
* Reference:
* https://en.wikipedia.org/wiki/360-day_calendar
* https://wiki.openoffice.org/wiki/Documentation/How_Tos/Calc:_Date_&_Time_functions#Financial_date_systems
*                  0 to calculate using the US/NASD method, with Excel compatibility (default)
*                  1 to calculate using the European method
*                  2 to calculate using the US/NASD method
* @returns {Number} number of days
*/
const days360 = (startDate, endDate, method) => {
	const start = isNumber(startDate) && startDate >= 0 ? new Date(startDate) : startDate;
	if (!isDate(start)) return;
	const end = isNumber(endDate) && endDate >= 0 ? new Date(endDate) : endDate;
	if (!isDate(end)) return;
	let startDay = start.getUTCDate();
	let endDay = end.getUTCDate();
	if (method === 1) {
		startDay = Math.min(startDay, 30);
		endDay = Math.min(endDay, 30);
	} else {
		/**
		* If both date A and B fall on the last day of February, then date B will be changed to
		* the 30th (unless preserving Excel compatibility)
		*/
		const isStartLast = isLastDay(start);
		if (method === 2 && isStartLast && isLastDay(end)) endDay = 30;
		/**
		* If date A falls on the 31st of a month or last day of February, then date A will be changed
		* to the 30th.
		*/
		if (isStartLast || startDay === 31) startDay = 30;
		/**
		* If date A falls on the 30th of a month after applying (2) above and date B falls on the
		* 31st of a month, then date B will be changed to the 30th.
		*/
		if (startDay === 30 && endDay === 31) endDay = 30;
	}
	return (end.getUTCFullYear() - start.getUTCFullYear()) * 360 + (end.getUTCMonth() - start.getUTCMonth()) * 30 + (endDay - startDay);
};
//#endregion
//#region src/errors.ts
const serializeError = (errorLike, options) => {
	if (typeof errorLike === "string") return serializeError$1(new Error(errorLike), options);
	if (errorLike?.isAxiosError) return serializeError$1({
		...omit(errorLike, "request", "config"),
		response: pick(errorLike, "response.data", "response.status")
	}, options);
	return serializeError$1(errorLike, options);
};
let ErrorSeverity = /* @__PURE__ */ function(ErrorSeverity) {
	ErrorSeverity["LOW"] = "LOW";
	ErrorSeverity["HIGH"] = "HIGH";
	ErrorSeverity["FATAL"] = "FATAL";
	return ErrorSeverity;
}({});
let ErrorTag = /* @__PURE__ */ function(ErrorTag) {
	ErrorTag["FRONTEND_SILENT"] = "FRONTEND_SILENT";
	return ErrorTag;
}({});
var BaseError = class BaseError extends BaseError$1 {
	code;
	retriable = false;
	severity = "HIGH";
	data;
	tags = [];
	errorPath;
	constructor(message, code, data, severity = "HIGH") {
		super(message);
		const { cause, ...restData } = data || {};
		if (cause) {
			if (cause.isAxiosError || cause?.response) this.cause = pick(cause, [
				"response",
				"code",
				"status"
			]);
			else this.cause = cause;
			this.errorPath = this.getErrorPath(this.cause);
		}
		this.code = code;
		this.data = Object.keys(restData).length ? restData : void 0;
		if (severity) this.severity = severity;
	}
	toJSON() {
		return serializeError(this);
	}
	getErrorPath(error, path = []) {
		if (!error) return null;
		if (error instanceof BaseError || error instanceof Error) {
			path.push(`${error.name}${"code" in error ? `(${error.code})` : ""}: ${error.message}`);
			if (error?.cause) {
				const innerPath = this.getErrorPath(error.cause);
				if (innerPath) path.push(...innerPath);
			}
		}
		return path;
	}
	static wrap(stdError, data) {
		const error = new BaseError(stdError.message, "Internal", data);
		error.name = stdError.name;
		if (stdError.stack) error.stack = stdError.stack;
		return error;
	}
	addData(data) {
		this.data = {
			...this.data,
			...data
		};
	}
};
var RetriableError = class extends BaseError {
	constructor(message, code, data, severity) {
		super(message, code, data, severity);
		this.retriable = true;
	}
};
var NonRetriableError = class extends BaseError {
	constructor(message, code, data, severity) {
		super(message, code, data, severity);
		this.retriable = false;
	}
};
const getInnerMostError = (e) => {
	if (e?.response?.data) return getInnerMostError(e.response.data);
	return e?.data?.context?.cause ?? e?.data?.context?.errors?.[0] ?? e?.data?.context ?? e?.data ?? e;
};
const getRootCauseErrorIfExists = (error) => {
	if (error?.cause) return getRootCauseErrorIfExists(error.cause);
	return error;
};
const getFullErrorCauseMessage = (error, separator = ", ", message = "") => {
	if (error?.cause) return getFullErrorCauseMessage(error.cause, separator, `${message}${separator}${error.message}`);
	return `${message}${separator}${error.message}`;
};
const errorToLogSeverity = {
	["LOW"]: "warn",
	["HIGH"]: "error",
	["FATAL"]: "fatal"
};
function getLogSeverity(error) {
	return errorToLogSeverity[error.severity] ?? "error";
}
const findMatchingErrorsInChain = (error, messageContent, matchingErrors = []) => {
	if (error.message?.includes(messageContent)) matchingErrors.push(error);
	if (error.cause) findMatchingErrorsInChain(error.cause, messageContent, matchingErrors);
	return matchingErrors;
};
//#endregion
//#region src/fast-cartesian.ts
const validateInputs = (inputs) => {
	if (!Array.isArray(inputs)) throw new TypeError("Argument must be an array of arrays");
	inputs.forEach(validateInput);
	validateDimensions(inputs);
	validateCombinations(inputs);
};
const validateInput = (input) => {
	if (!Array.isArray(input)) throw new TypeError(`Argument must be an array: ${input}`);
};
const validateDimensions = ({ length }) => {
	if (length >= MAX_DIMENSIONS) throw new TypeError(`Too many arrays (${length}): please use the 'big-cartesian' library instead of 'fast-cartesian'`);
};
const MAX_DIMENSIONS = 100;
const validateCombinations = (inputs) => {
	const size = inputs.reduce(multiplySize, 1);
	if (size >= MAX_SIZE) {
		const sizeStr = Number.isFinite(size) ? ` (${size.toExponential(0)})` : "";
		throw new TypeError(`Too many combinations${sizeStr}: please use the 'big-cartesian' library instead of 'fast-cartesian'`);
	}
};
const multiplySize = (size, input) => size * input.length;
const MAX_SIZE = 2 ** 32;
const getLoopFunc = (length) => {
	const cachedLoopFunc = cache[length];
	if (cachedLoopFunc !== void 0) return cachedLoopFunc;
	const loopFunc = mGetLoopFunc(length);
	cache[length] = loopFunc;
	return loopFunc;
};
const cache = {};
const mGetLoopFunc = (length) => {
	const indexes = Array.from({ length }, getIndex);
	const start = indexes.map((index) => `for (const value${index} of arrays[${index}]) {`).join("\n");
	const middle = indexes.map((index) => `value${index}`).join(", ");
	const end = "}\n".repeat(length);
	return new Function("arrays", "result", `${start}\nresult.push([${middle}])\n${end}`);
};
const getIndex = (_value, index) => String(index);
const fastCartesian = (inputs) => {
	validateInputs(inputs);
	const result = [];
	if (inputs.length === 0) return result;
	getLoopFunc(inputs.length)(inputs, result);
	return result;
};
//#endregion
//#region src/strings.ts
const fuzzyMatch = (name, filter) => {
	if (filter === "") return true;
	const filterLower = filter.toLowerCase();
	const nameLower = name.toLowerCase();
	let filterIndex = 0;
	for (let i = 0; i < nameLower.length && filterIndex < filterLower.length; i++) if (nameLower[i] === filterLower[filterIndex]) filterIndex++;
	return filterIndex === filterLower.length;
};
/**
* Levenshtein distance implementation (from https://github.com/gustf/js-levenshtein/blob/master/index.js)
*/
function levenshtein(a, b) {
	function _min(d0, d1, d2, bx, ay) {
		return d0 < d1 || d2 < d1 ? d0 > d2 ? d2 + 1 : d0 + 1 : bx === ay ? d1 : d1 + 1;
	}
	if (a === b) return 0;
	if (a.length > b.length) {
		const tmp = a;
		a = b;
		b = tmp;
	}
	let la = a.length;
	let lb = b.length;
	while (la > 0 && a.charCodeAt(la - 1) === b.charCodeAt(lb - 1)) {
		la--;
		lb--;
	}
	let offset = 0;
	while (offset < la && a.charCodeAt(offset) === b.charCodeAt(offset)) offset++;
	la -= offset;
	lb -= offset;
	if (la === 0 || lb < 3) return lb;
	let x = 0;
	let y;
	let d0;
	let d1;
	let d2;
	let d3;
	let dd;
	let dy;
	let ay;
	let bx0;
	let bx1;
	let bx2;
	let bx3;
	const vector = [];
	for (y = 0; y < la; y++) {
		vector.push(y + 1);
		vector.push(a.charCodeAt(offset + y));
	}
	const len = vector.length - 1;
	for (; x < lb - 3;) {
		bx0 = b.charCodeAt(offset + (d0 = x));
		bx1 = b.charCodeAt(offset + (d1 = x + 1));
		bx2 = b.charCodeAt(offset + (d2 = x + 2));
		bx3 = b.charCodeAt(offset + (d3 = x + 3));
		dd = x += 4;
		for (y = 0; y < len; y += 2) {
			dy = vector[y];
			ay = vector[y + 1];
			d0 = _min(dy, d0, d1, bx0, ay);
			d1 = _min(d0, d1, d2, bx1, ay);
			d2 = _min(d1, d2, d3, bx2, ay);
			dd = _min(d2, d3, dd, bx3, ay);
			vector[y] = dd;
			d3 = d2;
			d2 = d1;
			d1 = d0;
			d0 = dy;
		}
	}
	for (; x < lb;) {
		bx0 = b.charCodeAt(offset + (d0 = x));
		dd = ++x;
		for (y = 0; y < len; y += 2) {
			dy = vector[y];
			vector[y] = dd = _min(dy, d0, dd, bx0, vector[y + 1]);
			d0 = dy;
		}
	}
	return dd;
}
const extraChars = [
	["Ğ", "G"],
	["Ü", "U"],
	["Ş", "S"],
	["I", "I"],
	["İ", "I"],
	["Ö", "O"],
	["Ç", "C"],
	["ğ", "g"],
	["ü", "u"],
	["ş", "s"],
	["ș", "s"],
	["ı", "i"],
	["ö", "o"],
	["ç", "c"]
];
const deburr = (str) => {
	const deburred = _deburr(str);
	return extraChars.reduce((acc, [char, replacement]) => acc.replaceAll(char, replacement), deburred);
};
/**
* Lower-cases, strips diacritics and collapses separators to produce a stable, comparable string.
* Useful for fuzzy matching, lookups and slug-like keys. Returns '' for nullish input.
*/
const normalizeText = (value, options = {}) => {
	if (value == null) return "";
	const { form = "NFD", expandEszett = false, collapse = "alnum" } = options;
	let result = value.toString().normalize(form).replace(/[\u0300-\u036f]/g, "");
	if (expandEszett) result = result.replace(/ß/g, "ss");
	result = result.toLowerCase();
	if (collapse === "alnum") result = result.replace(/[^a-z0-9]+/g, " ");
	else result = result.replace(/[\s\u00A0_-]+/g, " ");
	return result.trim();
};
//#endregion
//#region src/finance.helpers.ts
const prepareCreditorName = (name) => deburr(name).substring(0, 70);
//#endregion
//#region src/fp.ts
const not = (predicate) => (value) => !predicate(value);
//#endregion
//#region src/json-helpers.ts
const differ = jsondiffpatch.create();
function safeJsonParse(val, options) {
	try {
		return JSON.parse(val);
	} catch {
		return options?.onFailure === "undefined" ? void 0 : val;
	}
}
const getCircularJsonReplacer = () => {
	const seen = /* @__PURE__ */ new WeakSet();
	return (_key, value) => {
		if (typeof value === "object" && value !== null) {
			if (seen.has(value)) return;
			seen.add(value);
		}
		return value;
	};
};
const getJSONDiff = (left, right) => differ.diff(left, right);
/**
* As we can send partial objects to update, diff will assume that we deleted some keys.
* We need to ignore that fact as it is not possible to delete any field in DB
* @link https://github.com/benjamine/jsondiffpatch/blob/ef38c5d97c349b0a3672c08710d1625307d3de94/docs/deltas.md
* @param delta
*/
const getObjectDelta = (left, right, omittedKeys) => {
	const delta = getJSONDiff(left, right) ?? {};
	const changedDelta = {};
	for (const key of Object.keys(delta)) {
		if (Array.isArray(delta[key]) && delta[key].length === 1) continue;
		if (Array.isArray(delta[key]) && delta[key].length > 2 && delta[key][1] === 0 && delta[key][2] === 0) continue;
		changedDelta[key] = delta[key];
	}
	return omittedKeys?.length ? omit(changedDelta, omittedKeys) : changedDelta;
};
//#endregion
//#region src/machine-helpers.ts
const getPossibleTransitions = (machine, currentState, eventBlacklist) => {
	if (!machine || !currentState) return [];
	const { id: machineName, type } = machine;
	let { states } = machine;
	const transitions = [];
	if (type === "parallel" || typeof currentState === "object") {
		const rootParallelName = Object.keys(currentState)[0];
		const stateToParse = type === "parallel" ? currentState : currentState[rootParallelName];
		if (type !== "parallel") states = machine.states[rootParallelName].states;
		return Object.entries(states).reduce((result, [group, groupMachine]) => {
			result[group] = getPossibleTransitions({
				id: machineName,
				...groupMachine
			}, stateToParse[group]);
			return result;
		}, {});
	}
	Object.keys(states).forEach((state) => {
		if (state !== currentState) return;
		if (!states[state].on || states[state]?.type === "final") return;
		Object.entries(states[state].on).forEach((group) => {
			let [event, target] = group;
			let targetState;
			let meta = {};
			if (typeof target === "string") targetState = target.replace(`#${machineName}.`, "");
			else if (Array.isArray(target)) {
				target = target.filter((t) => !t.meta?.automatic);
				if (target.length) {
					const targetWithStates = target.filter((t) => !!t.target);
					if (targetWithStates.length) targetState = targetWithStates.map((t) => {
						const target = t.target.replace(`#${machineName}.`, "");
						return {
							cond: typeof t.cond === "string" ? t.cond : t?.cond?.type,
							target
						};
					});
				}
			} else {
				if (target.meta?.automatic) return;
				targetState = target.target;
				meta = target.meta ?? {};
			}
			if (targetState && event !== "") transitions.push({
				event,
				target: targetState,
				meta
			});
		});
	});
	return transitions.filter((t) => !t.meta?.automatic && (eventBlacklist ? !eventBlacklist.includes(t.event) : true));
};
//#endregion
//#region src/maps.ts
const GERMANY_COORDS = {
	lat: 48.1657,
	lng: 10.4515
};
const getCenterFromCoordinates = (coordinates) => {
	if (!coordinates.length) return GERMANY_COORDS;
	return {
		lat: coordinates.reduce((acc, current) => acc + current.lat, 0) / coordinates.length,
		lng: coordinates.reduce((acc, current) => acc + current.lng, 0) / coordinates.length
	};
};
//#endregion
//#region src/math-set.ts
var MathSet = class MathSet {
	a;
	static fromArray(arr) {
		return new MathSet(new Set(arr));
	}
	static intersection(...sets) {
		return new MathSet(new Set(intersection(...sets.map((v) => v.toArray()))));
	}
	constructor(a) {
		this.a = a;
	}
	subtractSet(b) {
		for (const v of this.a) if (b.has(v)) this.a.delete(v);
		return this;
	}
	addSet(b) {
		for (const v of b.toArray()) this.a.add(v);
		return this;
	}
	add(item) {
		this.a.add(item);
		return this;
	}
	toArray() {
		return Array.from(this.a);
	}
	flatMap(fn) {
		return new MathSet(new Set(Array.from(this.a).flatMap(fn)));
	}
	clone() {
		return new MathSet(new Set(Array.from(this.a)));
	}
	has(v) {
		return this.a.has(v);
	}
	delete(v) {
		return this.a.delete(v);
	}
	get size() {
		return this.a.size;
	}
	[Symbol.iterator]() {
		return this.a[Symbol.iterator]();
	}
};
//#endregion
//#region src/normalize-trace-id.ts
const normalizeTrace = (traceId) => {
	if (!traceId?.length) return;
	return /^[a-z0-9]{2}-([a-z0-9]*)-[a-z0-9]*-[a-z0-9]{2}$/.exec(traceId)?.[1] ?? traceId;
};
//#endregion
//#region src/normalized-mfh-address.ts
const HOUSE_NUMBER_TOKEN = /^\d+[a-zA-Z]?$/;
const combineNormalizedMfhAddress = ({ street, city, zipcode, houseNumber }) => Buffer.from(JSON.stringify({
	city,
	houseNumber,
	postalCode: zipcode,
	street
}), "utf8").toString("base64");
const isFullNormalizedMfhAddress = (location) => Boolean(location.street && location.houseNumber && location.zipcode && location.city);
/** Comma-separated house numbers (`12` or `12, 12a`). `undefined` means the string is not a valid list. */
const parseHouseNumberList = (value) => {
	const trimmed = value.trim();
	if (!trimmed) return [];
	if (trimmed.includes(";")) return void 0;
	const tokens = trimmed.split(",").map((part) => part.trim());
	if (tokens.some((token) => token.length === 0) || !tokens.every((token) => HOUSE_NUMBER_TOKEN.test(token))) return;
	return uniqBy(tokens, (token) => token.toLowerCase());
};
//#endregion
//#region src/params-type-caster.ts
const paramsTypeCaster = (filter, paramTypes) => {
	const nameRouteParam = Object.entries(paramTypes).reduce((acc, [name, fn]) => !isNil(filter[name]) ? {
		...acc,
		[name]: Array.isArray(filter[name]) ? filter[name].map((e) => fn(e)) : fn(filter[name])
	} : acc, {});
	return {
		...filter,
		...nameRouteParam
	};
};
//#endregion
//#region src/sentry-user-context.ts
function displayName(user) {
	const parts = [user.firstName, user.lastName].filter((p) => Boolean(p?.trim()));
	if (parts.length) return parts.join(" ");
	return user.email;
}
/**
* Builds a plain Sentry user context object from any user with a `uid`.
* Works with both IRequestUser (backend) and IUser (frontend) — no Sentry dependency.
* Returns an empty object when `user` is missing or has no `uid`.
*/
function buildSentryUserContext(user) {
	if (!user?.uid) return {};
	const tags = {};
	if (user.impersonatedUserUid) tags.impersonated_user_uid = user.impersonatedUserUid;
	if (user.requestingService) tags.requesting_service = String(user.requestingService);
	if (user.activeRole) tags.user_active_role = user.activeRole;
	if (user.signInProvider) tags.user_sign_in_provider = user.signInProvider;
	if (user.companyFunction) tags.user_company_function = String(user.companyFunction);
	if (user.contactType) tags.user_contact_type = String(user.contactType);
	return {
		user: {
			id: user.uid,
			email: user.email,
			username: displayName(user)
		},
		...Object.keys(tags).length ? { tags } : {}
	};
}
//#endregion
//#region src/tree.ts
const replaceTree = (obj, replaceFunc, pathMemo = []) => {
	if (Array.isArray(obj)) return obj.map((o, i) => replaceTree(o, replaceFunc, [...pathMemo, i]));
	if (isPlainObject(obj)) return Object.entries(obj).reduce((acc, [k, v]) => {
		if (Array.isArray(v)) return {
			...acc,
			[k]: v.map((val, i) => replaceTree(val, replaceFunc, [
				...pathMemo,
				k,
				i
			]))
		};
		if (isPlainObject(v)) return {
			...acc,
			[k]: replaceTree(v, replaceFunc, [...pathMemo, k])
		};
		return {
			...acc,
			[k]: replaceFunc(k, v, [...pathMemo, k].join("."))
		};
	}, {});
	return obj;
};
const deepWalkBy = (obj, replaceFn, memo = []) => {
	if (!isPlainObject(obj)) return obj;
	return Object.entries(obj).reduce((acc, [k, v]) => {
		let value = v;
		const nextMemo = [...memo, k];
		if (isPlainObject(v)) value = deepWalkBy(v, replaceFn, nextMemo);
		else if (isArray(v)) value = v.map((el, i) => deepWalkBy(el, replaceFn, [...nextMemo, i]));
		const result = replaceFn(k, value, joinPath(nextMemo));
		return result ? {
			...acc,
			...result
		} : acc;
	}, {});
};
const deepOmitBy = (obj, shouldStrip, ignoredKeys, skipKeys) => deepWalkBy(obj, (k, v) => {
	if (skipKeys?.includes(k)) return { [k]: v };
	if (shouldStrip(v)) return;
	if (ignoredKeys?.includes(k)) return;
	return { [k]: v };
});
const isNumLike = (numLike) => typeof numLike === "number" || /\d+/.test(numLike.trim());
const joinPath = (path) => path.map((p) => isNumLike(p) ? `[${p}]` : `.${p}`).join("").replace(/^\./, "");
const deepCollectKeys = (obj, predicate, ignoredKeys) => {
	const result = [];
	deepWalkBy(obj, (k, v, fullPath) => {
		if (predicate(v, k, fullPath) && (!ignoredKeys || !ignoredKeys.includes(k))) result.push(fullPath);
		return { [k]: v };
	});
	return result;
};
const defaultShouldWalk = (value, _key, _path) => isPlainObject(value) || isArray(value);
const collectLeaves = (obj, prefix = "", shouldWalk = defaultShouldWalk) => {
	const paths = [];
	for (const key in obj) {
		const value = obj[key];
		const currentPath = prefix ? `${prefix}.${key}` : key;
		if (shouldWalk(value, key, currentPath)) paths.push(...collectLeaves(value, currentPath, shouldWalk));
		else paths.push(currentPath);
	}
	return paths;
};
const mergeAndKeepArrays = (a, b) => {
	return mergeWith({}, a, b, (objValue, srcValue) => {
		if (Array.isArray(objValue)) return srcValue;
	});
};
const mergeWithArrays = (a, b) => {
	return mergeWith({}, a, b, (objValue, srcValue) => {
		if (Array.isArray(objValue) && Array.isArray(srcValue)) return union(objValue, srcValue);
	});
};
const deepOmitByNil = (obj, ignoredKeys, skipKeys) => deepOmitBy(obj, isNil, ignoredKeys, skipKeys);
const camelizeAllKeys = (obj, prefixToRemove) => {
	if (!isPlainObject(obj)) return obj;
	return Object.entries(obj).reduce((acc, [k, v]) => {
		let value = v;
		if (isPlainObject(v)) value = camelizeAllKeys(v, prefixToRemove);
		else if (isArray(v)) value = v.map((el) => camelizeAllKeys(el, prefixToRemove));
		const key = prefixToRemove ? camelCase(k.replace(new RegExp(`^${prefixToRemove}`), "")) : camelCase(k);
		return {
			...acc,
			[key]: value
		};
	}, {});
};
//#endregion
//#region src/validators.ts
const isBicValid = (bic) => {
	return !!bic && (bic.length === 11 || bic.length === 8);
};
const isEmail = (s) => {
	return /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(s);
};
//#endregion
export { BaseError, BasecampFilterSpecifics, COL_SEPARATOR, DateFormats, EMPTY_VAL, EU, ErrorSeverity, ErrorTag, GERMANY_COORDS, LazyPromise, MathSet, NonRetriableError, ROW_SEPARATOR, RetriableError, US, US_NASD, arePeriodsOverlapping, arrayify, arrify, asyncRetry, batchedConcurrentAll, batchedConcurrentAllSettled, buildSentryUserContext, calculateDiffInYears, calculatePartialMonthRatio, camelizeAllKeys, capitalize, cleanNonPrintableChars, cleanStreetName, collectLeaves, combineNormalizedMfhAddress, comparisonRatioPercentage, compressResponse, createIban, dateStringToSpreadsheetDate, dateToSpreadsheetDate, days360, deburr, decapitalize, decompressResponse, deepCollectKeys, deepOmitBy, deepOmitByNil, deepWalkBy, definedAndEquals, diff, downscaleImageFiles, energyCategoryMapper, ensureMinimumTime, enumPascal, escapeNumber, estimateJSONByteSize, extractDate, extractIBAN, fastCartesian, findMatchingErrorsInChain, firstFiniteNumber, firstNonEmptyString, flattenJSON, floatBiggerThan, floatEquals, floatSmallerOrEqualThan, floor, formatDate, fuzzyMatch, generateRandomPassword, getAddressLine, getCCSBaseUrl, getCenterFromCoordinates, getCircularJsonReplacer, getCourtageMultiplier, getFreeFromCategory, getFullErrorCauseMessage, getGDriveLink, getInnerMostError, getInngestUrl, getJSONDiff, getLogSeverity, getMonthlyChunks, getObjectDelta, getPossibleTransitions, getPrefixSumArrayWithInflation, getPsValue, getRootCauseErrorIfExists, getYearsSinceRenovation, ibanChecksum, isBefore, isBicValid, isEmail, isFullNormalizedMfhAddress, isOpenDateIntervalsOverlapping, isPresent, isRecord, isString, isToday, iso13616Prepare, joinPath, keysToCamelCase, knownDomains, levenshtein, median, mergeAndKeepArrays, mergeWithArrays, normalizeText, normalizeTrace, not, paramsTypeCaster, parseFloatLikeString, parseHouseNumberList, percentile, prepareCreditorName, prune, replaceAllWithMap, replacePathname, replaceTree, round, roundAccurately, roundByBankersMethod, roundToNearest, roundToNearestMinute, roundUpToNearest, safeDifference, safeDivide, safeJsonParse, safeMul, safeSum, safeTry, sameDay, serializeError, sleep, sortByDate, splitAmountAcrossAllocations, stripEmptyFields, subSort, superDelta, toBoolean, transpose, transposeObjects, umlautMap, undefinedWhenEmpty, valOrNil, withTimeout, xor };

//# sourceMappingURL=index.mjs.map